// AI Insights Engine for BuildMyShifts
// Generates scheduling recommendations based on historic data
// All data is scoped to the organisation's tenant — no cross-tenant leakage

import { getShiftDurationHours, validateShiftAssignment, getWeeklyHours, getWeekStart, validateBlockAssignment, getBlockDurationHours } from './schedulingEngine';
import { SEGMENT_TYPES, getBlockWorkingHours } from './shiftTypes';
import { calculateFatigueScore } from './fatigueEngine';
import { rankByRoleFit, canCoverRole } from './roleHierarchy';
import { buildDistribution, analyseFairness } from './fairnessEngine';

/**
 * Confidence levels for AI recommendations
 */
export const CONFIDENCE = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
};

/**
 * Calculate a staff member's reliability score from historical data
 * Returns a score 0–100
 */
export function calculateReliabilityScore(staffHistory) {
  if (!staffHistory || staffHistory.length === 0) return 50; // Neutral for new staff

  const total = staffHistory.length;
  const confirmed = staffHistory.filter(h => h.confirmed).length;
  const noShows = staffHistory.filter(h => h.noShow).length;
  const swapRequests = staffHistory.filter(h => h.swapRequested).length;
  const declines = staffHistory.filter(h => h.declined).length;

  const confirmRate = confirmed / total;
  const noShowRate = noShows / total;
  const swapRate = swapRequests / total;
  const declineRate = declines / total;

  // Weighted formula
  const score = Math.round(
    (confirmRate * 40) +
    ((1 - noShowRate) * 30) +
    ((1 - swapRate) * 15) +
    ((1 - declineRate) * 15)
  );

  return Math.max(0, Math.min(100, score));
}

/**
 * Calculate shift preference score for a staff member
 * How well does this shift match their usual patterns?
 */
export function calculatePreferenceScore(staffId, shift, historicShifts) {
  const pastShifts = historicShifts.filter(h => h.assignedStaffId === staffId);
  if (pastShifts.length < 3) return 50; // Not enough data

  // Day-of-week match
  const shiftDay = new Date(shift.date).getDay();
  const dayMatches = pastShifts.filter(h => new Date(h.date).getDay() === shiftDay).length;
  const dayScore = (dayMatches / pastShifts.length) * 100;

  // Time-of-day similarity (± 2 hours)
  const shiftHour = parseInt(shift.startTime.split(':')[0]);
  const timeMatches = pastShifts.filter(h => {
    const hHour = parseInt(h.startTime.split(':')[0]);
    return Math.abs(hHour - shiftHour) <= 2;
  }).length;
  const timeScore = (timeMatches / pastShifts.length) * 100;

  // Role match
  const roleMatches = pastShifts.filter(h => h.requiredRole === shift.requiredRole).length;
  const roleScore = shift.requiredRole ? (roleMatches / pastShifts.length) * 100 : 50;

  // Weighted total
  return Math.round(dayScore * 0.3 + timeScore * 0.4 + roleScore * 0.3);
}

/**
 * Calculate fairness/load-balance score
 * Higher score = this person has worked fewer hours recently (more available)
 */
export function calculateFairnessScore(staffId, shift, allStaff, existingShifts) {
  const weekStart = getWeekStart(shift.date);

  // Get hours for all staff this week
  const hoursMap = {};
  let maxHours = 0;
  let minHours = Infinity;

  for (const s of allStaff) {
    const hours = getWeeklyHours(s.id, weekStart, existingShifts);
    hoursMap[s.id] = hours;
    maxHours = Math.max(maxHours, hours);
    minHours = Math.min(minHours, hours);
  }

  if (maxHours === minHours) return 50; // All equal — neutral

  const staffHours = hoursMap[staffId] || 0;
  // Invert: fewer hours = higher fairness score
  const range = maxHours - minHours;
  return Math.round(((maxHours - staffHours) / range) * 100);
}

/**
 * Generate auto-fill suggestions for a shift
 * Returns ranked list of staff recommendations
 */
export function generateAutoFillSuggestions(shift, allStaff, existingShifts, staffHistories, staffProfiles, config, filters = {}) {
  const suggestions = [];

  for (const staff of allStaff) {
    if (!staff.active) continue;

    // Run validation
    const profile = staffProfiles[staff.id] || {};
    const validation = validateShiftAssignment(staff.id, shift, existingShifts, profile, config);

    if (!validation.valid) {
      // Still include as "unavailable" with reason
      suggestions.push({
        staffId: staff.id,
        staffName: staff.displayName || staff.username,
        available: false,
        reasons: validation.errors,
        score: 0,
        confidence: CONFIDENCE.HIGH,
        confidenceScore: 0,
      });
      continue;
    }

    // Calculate component scores
    const history = staffHistories[staff.id] || [];
    const reliability = calculateReliabilityScore(history);
    const preference = calculatePreferenceScore(staff.id, shift, existingShifts);
    const fairness = calculateFairnessScore(staff.id, shift, allStaff, existingShifts);

    // Skills/role match bonus
    let skillScore = 50;
    if (shift.requiredRole && profile.roles?.includes(shift.requiredRole)) {
      skillScore = 100;
    } else if (shift.requiredRole && !profile.roles?.includes(shift.requiredRole)) {
      skillScore = 10;
    }
    if (shift.requiredSkills?.length > 0 && profile.skills) {
      const matched = shift.requiredSkills.filter(sk => profile.skills.includes(sk)).length;
      skillScore = (matched / shift.requiredSkills.length) * 100;
    }

    // Contract hours consideration
    let contractScore = 50;
    if (profile.contractHours) {
      const weekStart = getWeekStart(shift.date);
      const currentHours = getWeeklyHours(staff.id, weekStart, existingShifts);
      const shiftHours = getShiftDurationHours(shift);
      const remaining = profile.contractHours - currentHours;
      if (remaining >= shiftHours) {
        contractScore = 80;
      } else if (remaining > 0) {
        contractScore = 40;
      } else {
        contractScore = 10; // Already at or over contract hours
      }
    }

    // Apply filter weights
    let weights = {
      reliability: 0.25,
      preference: 0.20,
      fairness: 0.20,
      skills: 0.20,
      contract: 0.15,
    };

    if (filters.preferLowHours) {
      weights.fairness = 0.40;
      weights.reliability = 0.20;
      weights.preference = 0.15;
      weights.skills = 0.15;
      weights.contract = 0.10;
    }
    if (filters.preferExperienced) {
      weights.preference = 0.35;
      weights.reliability = 0.30;
      weights.fairness = 0.10;
      weights.skills = 0.15;
      weights.contract = 0.10;
    }
    if (filters.avoidOvertime) {
      weights.contract = 0.35;
      weights.fairness = 0.25;
      weights.reliability = 0.15;
      weights.preference = 0.15;
      weights.skills = 0.10;
    }
    if (filters.prioritiseFairness) {
      weights.fairness = 0.45;
      weights.reliability = 0.15;
      weights.preference = 0.15;
      weights.skills = 0.15;
      weights.contract = 0.10;
    }

    // Composite score
    const score = Math.round(
      reliability * weights.reliability +
      preference * weights.preference +
      fairness * weights.fairness +
      skillScore * weights.skills +
      contractScore * weights.contract
    );

    // Determine confidence
    let confidence = CONFIDENCE.LOW;
    let confidenceScore = 0;
    const dataPoints = history.length;
    if (dataPoints >= 20 && score >= 70) {
      confidence = CONFIDENCE.HIGH;
      confidenceScore = Math.min(95, score);
    } else if (dataPoints >= 5 && score >= 50) {
      confidence = CONFIDENCE.MEDIUM;
      confidenceScore = Math.min(75, score);
    } else {
      confidence = CONFIDENCE.LOW;
      confidenceScore = Math.min(50, score);
    }

    // Build reason list
    const reasonList = [];
    if (reliability >= 80) reasonList.push('High reliability');
    if (preference >= 70) reasonList.push('Matches usual shift pattern');
    if (fairness >= 70) reasonList.push('Balances workload (fewer hours this week)');
    if (skillScore >= 80) reasonList.push('Strong skills/role match');
    if (contractScore >= 70) reasonList.push('Within contract hours');
    if (validation.warnings.length > 0) reasonList.push(...validation.warnings);

    suggestions.push({
      staffId: staff.id,
      staffName: staff.displayName || staff.username,
      available: true,
      score,
      confidence,
      confidenceScore,
      reasonList,
      warnings: validation.warnings,
      breakdown: {
        reliability,
        preference,
        fairness,
        skillScore,
        contractScore,
      },
    });
  }

  // Sort by score descending, available first
  suggestions.sort((a, b) => {
    if (a.available && !b.available) return -1;
    if (!a.available && b.available) return 1;
    return b.score - a.score;
  });

  return suggestions;
}

/**
 * Coverage forecasting — flag likely coverage gaps
 * Analyses historic demand patterns to predict staffing needs
 */
export function forecastCoverageGaps(weekStart, historicShifts, currentShifts, organisationConfig = {}) {
  const gaps = [];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Build a demand model from historic data
  // Group by day-of-week and time slot (2-hour blocks)
  const demandModel = {};

  for (const shift of historicShifts) {
    const day = new Date(shift.date).getDay();
    const startHour = parseInt(shift.startTime.split(':')[0]);
    const block = Math.floor(startHour / 2) * 2; // 2-hour blocks
    const key = `${day}-${block}`;

    if (!demandModel[key]) {
      demandModel[key] = { count: 0, weeks: new Set(), totalStaff: 0 };
    }
    demandModel[key].count++;
    demandModel[key].weeks.add(shift.date.substring(0, 10));
    demandModel[key].totalStaff++;
  }

  // For each block in the upcoming week, check if current coverage meets historic demand
  const startDate = new Date(weekStart);
  for (let d = 0; d < 7; d++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + d);
    const dateStr = date.toISOString().split('T')[0];
    const day = date.getDay();

    for (let block = 0; block < 24; block += 2) {
      const key = `${day}-${block}`;
      const model = demandModel[key];
      if (!model || model.count < 2) continue; // Not enough data

      const avgStaff = model.totalStaff / Math.max(1, model.weeks.size);
      const currentCoverage = currentShifts.filter(s => {
        if (s.date !== dateStr) return false;
        if (s.status === 'cancelled') return false;
        const h = parseInt(s.startTime.split(':')[0]);
        return h >= block && h < block + 2;
      }).length;

      if (currentCoverage < avgStaff * 0.8) {
        gaps.push({
          date: dateStr,
          dayName: dayNames[day],
          timeBlock: `${String(block).padStart(2, '0')}:00–${String(block + 2).padStart(2, '0')}:00`,
          expectedStaff: Math.round(avgStaff * 10) / 10,
          currentStaff: currentCoverage,
          shortfall: Math.ceil(avgStaff) - currentCoverage,
          severity: currentCoverage === 0 ? 'critical' : 'warning',
        });
      }
    }
  }

  return gaps.sort((a, b) => {
    if (a.severity === 'critical' && b.severity !== 'critical') return -1;
    if (a.severity !== 'critical' && b.severity === 'critical') return 1;
    return b.shortfall - a.shortfall;
  });
}

/**
 * Identify risky shifts — historically high decline/swap rates
 */
export function identifyRiskyShifts(weekStart, historicShifts) {
  const riskModel = {};

  for (const shift of historicShifts) {
    const day = new Date(shift.date).getDay();
    const startHour = parseInt(shift.startTime.split(':')[0]);
    const key = `${day}-${startHour}`;

    if (!riskModel[key]) {
      riskModel[key] = { total: 0, declined: 0, swapped: 0 };
    }
    riskModel[key].total++;
    if (shift.declined) riskModel[key].declined++;
    if (shift.swapRequested) riskModel[key].swapped++;
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const risks = [];

  for (const [key, data] of Object.entries(riskModel)) {
    if (data.total < 5) continue;
    const declineRate = data.declined / data.total;
    const swapRate = data.swapped / data.total;

    if (declineRate > 0.3 || swapRate > 0.3) {
      const [day, hour] = key.split('-').map(Number);
      risks.push({
        dayName: dayNames[day],
        hour: `${String(hour).padStart(2, '0')}:00`,
        declineRate: Math.round(declineRate * 100),
        swapRate: Math.round(swapRate * 100),
        totalHistoric: data.total,
        riskLevel: declineRate > 0.5 || swapRate > 0.5 ? 'high' : 'medium',
      });
    }
  }

  return risks.sort((a, b) => {
    const aScore = a.declineRate + a.swapRate;
    const bScore = b.declineRate + b.swapRate;
    return bScore - aScore;
  });
}

/**
 * Find the closest valid alternative staff for a shift
 * Used when the preferred staff member fails validation
 */
export function findAlternatives(shift, preferredStaffId, allStaff, existingShifts, staffProfiles, config) {
  const alternatives = [];

  for (const staff of allStaff) {
    if (staff.id === preferredStaffId) continue;
    if (!staff.active) continue;

    const profile = staffProfiles[staff.id] || {};
    const validation = validateShiftAssignment(staff.id, shift, existingShifts, profile, config);

    if (validation.valid) {
      alternatives.push({
        staffId: staff.id,
        staffName: staff.displayName || staff.username,
        warnings: validation.warnings,
      });
    }
  }

  return alternatives;
}

/**
 * Create an AI insight record for audit trail
 */
export function createInsightRecord(type, shiftId, recommendations, outcome = null) {
  return {
    type, // 'auto_fill' | 'coverage_gap' | 'risky_shift' | 'conflict_resolution'
    shiftId,
    recommendations,
    outcome, // 'accepted' | 'rejected' | 'modified' | null (pending)
    createdAt: new Date().toISOString(),
    version: '2.0',
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Enhanced AI — shift block, fatigue, role, fairness aware suggestions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calculate a sleep-in preference score for a staff member
 * Some staff prefer sleep-ins, others avoid them
 */
export function calculateSleepInPreference(staffId, historicShifts) {
  const pastShifts = historicShifts.filter(h => h.assignedStaffId === staffId);
  if (pastShifts.length < 3) return 50;

  const sleepInShifts = pastShifts.filter(h =>
    h.segments?.some(s => s.type === SEGMENT_TYPES.SLEEP_IN)
  );
  const sleepInRate = sleepInShifts.length / pastShifts.length;
  const confirmedSleepIns = sleepInShifts.filter(h => h.confirmed).length;
  const confirmedRate = sleepInShifts.length > 0 ? confirmedSleepIns / sleepInShifts.length : 0.5;

  // Higher score = more willing to do sleep-ins
  return Math.round(sleepInRate * 50 + confirmedRate * 50);
}

/**
 * Calculate premium shift fairness for a staff member
 * Checks if they're getting a fair share of premium/night/weekend shifts
 */
export function calculatePremiumFairness(staffId, allStaff, existingShifts) {
  const dist = buildDistribution(existingShifts);
  const analysis = analyseFairness(dist, 'premiumHours');

  const staffDist = dist[staffId];
  if (!staffDist) return 50;

  const mean = analysis.mean || 0;
  if (mean === 0) return 50;

  // If staff has fewer premium hours than average, they should get priority
  const ratio = staffDist.premiumHours / mean;
  if (ratio < 0.8) return 80; // Under-allocated — prioritise
  if (ratio > 1.2) return 20; // Over-allocated — deprioritise
  return 50; // Fair
}

/**
 * Enhanced auto-fill suggestions with fatigue, role hierarchy, and block awareness
 */
export function generateEnhancedSuggestions(shift, allStaff, existingShifts, staffHistories, staffProfiles, config, options = {}) {
  const suggestions = [];
  const isBlockShift = shift.segments && shift.segments.length > 0;
  const hasSleepIn = isBlockShift && shift.segments.some(s => s.type === SEGMENT_TYPES.SLEEP_IN);
  const hasOnCall = isBlockShift && shift.segments.some(s => s.type === SEGMENT_TYPES.ON_CALL);

  // Role-rank candidates first
  const roleRanked = options.customHierarchy
    ? rankByRoleFit(allStaff, shift.requiredRole, options.customHierarchy, options.customSubstitutions)
    : allStaff.map(s => ({ staff: s, roleScore: 50, isExactMatch: true, isSubstitute: false }));

  for (const { staff, roleScore, isExactMatch, isSubstitute } of roleRanked) {
    if (!staff.active) continue;

    // Validation (block-aware)
    const profile = staffProfiles[staff.id] || {};
    let validation;
    if (isBlockShift) {
      validation = validateBlockAssignment(staff.id, shift, existingShifts, profile, config);
    } else {
      validation = validateShiftAssignment(staff.id, shift, existingShifts, profile, config);
    }

    if (!validation.valid) {
      suggestions.push({
        staffId: staff.id,
        staffName: staff.displayName || staff.username,
        available: false,
        reasons: validation.errors,
        score: 0,
        confidence: CONFIDENCE.HIGH,
        confidenceScore: 0,
      });
      continue;
    }

    // Component scores
    const history = staffHistories[staff.id] || [];
    const reliability = calculateReliabilityScore(history);
    const preference = calculatePreferenceScore(staff.id, shift, existingShifts);
    const fairness = calculateFairnessScore(staff.id, shift, allStaff, existingShifts);

    // Fatigue score (inverted — lower fatigue = higher score)
    let fatigueScore = 80;
    try {
      const fatigue = calculateFatigueScore(staff.id, existingShifts.concat(shift), shift.date);
      fatigueScore = Math.max(0, 100 - fatigue.score);
    } catch (e) { /* Gracefully fall back */ }

    // Sleep-in preference (if applicable)
    let sleepInScore = 50;
    if (hasSleepIn) {
      sleepInScore = calculateSleepInPreference(staff.id, existingShifts);
    }

    // Premium fairness (if shift has premium elements)
    let premiumScore = 50;
    if (hasSleepIn || hasOnCall || shift.payType === 'nightPremium' || shift.payType === 'weekend') {
      premiumScore = calculatePremiumFairness(staff.id, allStaff, existingShifts);
    }

    // Skills match
    let skillScore = 50;
    if (shift.requiredRole && profile.roles?.includes(shift.requiredRole)) {
      skillScore = 100;
    } else if (shift.requiredRole && !profile.roles?.includes(shift.requiredRole)) {
      skillScore = isSubstitute ? 60 : 10;
    }
    if (shift.requiredSkills?.length > 0 && profile.skills) {
      const matched = shift.requiredSkills.filter(sk => profile.skills.includes(sk)).length;
      skillScore = (matched / shift.requiredSkills.length) * 100;
    }

    // Contract hours
    let contractScore = 50;
    if (profile.contractHours) {
      const weekStart = getWeekStart(shift.date);
      const currentHours = getWeeklyHours(staff.id, weekStart, existingShifts);
      const shiftHours = isBlockShift ? getBlockWorkingHours(shift.segments, true) : getShiftDurationHours(shift);
      const remaining = profile.contractHours - currentHours;
      contractScore = remaining >= shiftHours ? 80 : remaining > 0 ? 40 : 10;
    }

    // Weighted composite — adjusted for shift type
    let weights = {
      reliability: 0.15,
      preference: 0.10,
      fairness: 0.15,
      skills: 0.10,
      contract: 0.10,
      role: 0.10,
      fatigue: 0.15,
      sleepIn: hasSleepIn ? 0.10 : 0,
      premium: (hasSleepIn || hasOnCall) ? 0.05 : 0,
    };

    // Normalise weights to sum to 1
    const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
    Object.keys(weights).forEach(k => { weights[k] /= totalWeight; });

    // Apply filter overrides
    if (options.preferLowFatigue) {
      weights.fatigue = 0.35;
      weights.reliability = 0.15;
    }
    if (options.prioritiseFairness) {
      weights.fairness = 0.35;
      weights.premium = 0.15;
    }

    const score = Math.round(
      reliability * weights.reliability +
      preference * weights.preference +
      fairness * weights.fairness +
      skillScore * weights.skills +
      contractScore * weights.contract +
      roleScore * weights.role +
      fatigueScore * weights.fatigue +
      sleepInScore * weights.sleepIn +
      premiumScore * weights.premium
    );

    // Confidence
    let confidence = CONFIDENCE.LOW;
    let confidenceScore = 0;
    const dataPoints = history.length;
    if (dataPoints >= 20 && score >= 70) {
      confidence = CONFIDENCE.HIGH;
      confidenceScore = Math.min(95, score);
    } else if (dataPoints >= 5 && score >= 50) {
      confidence = CONFIDENCE.MEDIUM;
      confidenceScore = Math.min(75, score);
    } else {
      confidence = CONFIDENCE.LOW;
      confidenceScore = Math.min(50, score);
    }

    // Reasons
    const reasonList = [];
    if (isExactMatch) reasonList.push('Exact role match');
    if (isSubstitute) reasonList.push('Approved role substitute');
    if (reliability >= 80) reasonList.push('High reliability');
    if (preference >= 70) reasonList.push('Matches usual pattern');
    if (fairness >= 70) reasonList.push('Balances workload');
    if (fatigueScore >= 80) reasonList.push('Low fatigue risk');
    if (fatigueScore < 40) reasonList.push('⚠ Elevated fatigue risk');
    if (hasSleepIn && sleepInScore >= 70) reasonList.push('Willing to do sleep-ins');
    if (premiumScore >= 70) reasonList.push('Due more premium shifts');
    if (validation.warnings.length > 0) reasonList.push(...validation.warnings);

    suggestions.push({
      staffId: staff.id,
      staffName: staff.displayName || staff.username,
      available: true,
      score,
      confidence,
      confidenceScore,
      reasonList,
      warnings: validation.warnings,
      isExactMatch,
      isSubstitute,
      breakdown: {
        reliability,
        preference,
        fairness,
        skillScore,
        contractScore,
        roleScore,
        fatigueScore,
        sleepInScore: hasSleepIn ? sleepInScore : undefined,
        premiumScore: (hasSleepIn || hasOnCall) ? premiumScore : undefined,
      },
    });
  }

  suggestions.sort((a, b) => {
    if (a.available && !b.available) return -1;
    if (!a.available && b.available) return 1;
    return b.score - a.score;
  });

  return suggestions;
}

const aiInsights = {
  CONFIDENCE,
  calculateReliabilityScore,
  calculatePreferenceScore,
  calculateFairnessScore,
  calculateSleepInPreference,
  calculatePremiumFairness,
  generateAutoFillSuggestions,
  generateEnhancedSuggestions,
  forecastCoverageGaps,
  identifyRiskyShifts,
  findAlternatives,
  createInsightRecord,
};
export default aiInsights;
